/**
 * ETERNITY INTELLIGENCE — SIMPLIFIED HOME
 * Clean command overview, not information overload
 * Philosophy: LESS UI. MORE DIRECTION.
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const HomeSimplified = () => {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBriefing();
  }, []);

  const loadBriefing = async () => {
    try {
      const response = await api.get('/intelligence/briefing');
      setBriefing(response.data);
    } catch (error) {
      console.error('Failed to load briefing:', error);
    } finally {
      setLoading(false);
    }
  };

  const theme = {
    bg: '#0A0A0A',
    cardBg: '#141414',
    text: '#F4F2ED',
    textMuted: '#8A8A8A',
    ember: '#FC3B00',
    border: '#2A2A2A'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <div className="text-sm" style={{ color: theme.textMuted }}>Loading...</div>
      </div>
    );
  }

  // Mock data structure (replace with real briefing data)
  const todayItems = [
    { id: 1, title: 'Artwork Approval', type: 'approval', client: 'Aviah Financial' },
    { id: 2, title: 'Brand Book Draft', type: 'task', client: 'Sajoma' }
  ];

  const needsAttention = [
    { id: 1, title: 'Holiday Campaign Blocker', status: 'blocked', client: 'Sajoma' },
    { id: 2, title: 'Menu Photography', status: 'pending', client: 'Sajoma' }
  ];

  const approvals = [
    { id: 1, title: 'Website Post: Our Work', type: 'website' },
    { id: 2, title: 'Reel Concept', type: 'deliverable', client: 'Sajoma' }
  ];

  const recentActivity = [
    { id: 1, user: 'Maria', action: 'published', item: 'Systems post', time: '2h ago' },
    { id: 2, user: 'Samuel', action: 'approved', item: 'Brand guidelines', time: '3h ago' }
  ];

  return (
    <div className="min-h-screen px-4 py-5 md:p-8" style={{ backgroundColor: theme.bg, color: theme.text }}>
      <div className="max-w-4xl mx-auto">
        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5"
        >
          <h1 className="text-2xl md:text-4xl font-light tracking-tight mb-2">
            {briefing?.greeting || 'Good morning, Samuel.'}
          </h1>
          <p className="text-sm" style={{ color: theme.textMuted }}>
            {briefing?.contextual_intro || '3 items need your attention today.'}
          </p>
        </motion.div>

        {/* TODAY */}
        <Section
          title="TODAY"
          items={todayItems.slice(0, 3)}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-sm font-light">{item.title}</div>
                <div className="text-xs mt-1" style={{ color: theme.textMuted }}>{item.client}</div>
              </div>
              <ChevronRight size={16} style={{ color: theme.textMuted }} />
            </div>
          )}
          onViewAll={() => navigate('/intelligence/tasks')}
          theme={theme}
        />

        {/* NEEDS ATTENTION */}
        <Section
          title="NEEDS ATTENTION"
          items={needsAttention.slice(0, 2)}
          renderItem={(item) => (
            <div className="flex items-center gap-3">
              <AlertCircle size={18} style={{ color: theme.ember }} />
              <div className="flex-1">
                <div className="text-sm font-light">{item.title}</div>
                <div className="text-xs mt-1" style={{ color: theme.textMuted }}>
                  {item.client} • {item.status}
                </div>
              </div>
              <ChevronRight size={16} style={{ color: theme.textMuted }} />
            </div>
          )}
          onViewAll={() => navigate('/intelligence/tasks')}
          theme={theme}
        />

        {/* APPROVALS */}
        <Section
          title="APPROVALS"
          items={approvals.slice(0, 3)}
          renderItem={(item) => (
            <div className="flex items-center gap-3">
              <Clock size={18} style={{ color: '#FDA136' }} />
              <div className="flex-1">
                <div className="text-sm font-light">{item.title}</div>
                {item.client && (
                  <div className="text-xs mt-1" style={{ color: theme.textMuted }}>{item.client}</div>
                )}
              </div>
              <ChevronRight size={16} style={{ color: theme.textMuted }} />
            </div>
          )}
          onViewAll={() => navigate('/intelligence/website/approvals')}
          theme={theme}
        />

        {/* RECENT ACTIVITY */}
        <Section
          title="RECENT ACTIVITY"
          items={recentActivity.slice(0, 3)}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-sm font-light">
                  <span style={{ color: theme.ember }}>{item.user}</span> {item.action} {item.item}
                </div>
                <div className="text-xs mt-1" style={{ color: theme.textMuted }}>{item.time}</div>
              </div>
            </div>
          )}
          onViewAll={() => navigate('/intelligence/activity')}
          theme={theme}
        />
      </div>
    </div>
  );
};

// Reusable Section Component
const Section = ({ title, items, renderItem, onViewAll, theme }) => {
  if (!items || items.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[10px] font-medium tracking-wider" style={{ color: theme.textMuted }}>
          {title}
        </h2>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-xs font-light hover:underline"
            style={{ color: theme.ember }}
          >
            View all
          </button>
        )}
      </div>

      <div
        className="rounded-xl p-3 space-y-3"
        style={{
          backgroundColor: theme.cardBg,
          border: `1px solid ${theme.border}`
        }}
      >
        {items.map((item, index) => (
          <div key={item.id || index}>
            {renderItem(item)}
            {index < items.length - 1 && (
              <div className="h-px my-3" style={{ backgroundColor: theme.border }} />
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default HomeSimplified;
